import { escapeHtml } from '../core/formatters.js';
import { parseSyllabusLocally } from '../core/syllabusParser.js';
import { parseGradebook } from '../core/gradebookParser.js';
import { SyllabusService } from '../services/dataServices.js';

/**
 * Modal and toast host.
 *
 * Every interpolation of user-supplied text goes through `escapeHtml`. The previous version
 * injected parsed syllabus values and imported assignment names directly into template
 * literals, so pasting a syllabus containing `<img src=x onerror=...>` executed script in the
 * page — and the syllabus is exactly the field people paste unfamiliar content into.
 */

let container = null;
let escapeListener = null;

/** How long each status message is shown before the next one. */
const PROGRESS_INTERVAL_MS = 6000;

/** Cross-fade duration; must match the CSS transition on .ai-progress. */
const PROGRESS_FADE_MS = 220;

function ensureContainer() {
  if (!container) container = document.getElementById('modalContainer');
  return container;
}

function bindDismiss(onDismiss) {
  const host = ensureContainer();

  host.querySelector('.modal-backdrop')?.addEventListener('click', (event) => {
    if (event.target === event.currentTarget) onDismiss();
  });

  // Escape closes the dialog. Registered per-open and torn down on hide so dialogs cannot
  // stack listeners that fire after their own modal is gone.
  escapeListener = (event) => {
    if (event.key === 'Escape') onDismiss();
  };

  document.addEventListener('keydown', escapeListener);
}

export const Modal = {
  init() {
    container = document.getElementById('modalContainer');
  },

  show({ title, content, footer, wide = false }, onDismiss = () => this.hide()) {
    const host = ensureContainer();

    host.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal ${wide ? 'modal-lg' : ''}" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
          <div class="modal-header">
            <h2 class="modal-title">${escapeHtml(title)}</h2>
          </div>
          <div class="modal-body">${content}</div>
          ${footer ? `<div class="modal-footer">${footer}</div>` : ''}
        </div>
      </div>
    `;

    host.style.display = 'block';
    bindDismiss(onDismiss);

    // Move focus into the dialog so keyboard and screen-reader users are not left behind on
    // the page underneath.
    host.querySelector('input, textarea, button')?.focus();
  },

  hide() {
    const host = ensureContainer();

    // Always stop the status rotation: the dialog can be dismissed mid-request, and a timer
    // left running would tick against detached nodes for the life of the page.
    this._stopProgress();

    host.style.display = 'none';
    host.innerHTML = '';

    if (escapeListener) {
      document.removeEventListener('keydown', escapeListener);
      escapeListener = null;
    }
  },

  confirm({ title, message, confirmText = 'Confirm', danger = false }) {
    return new Promise((resolve) => {
      const settle = (value) => {
        this.hide();
        resolve(value);
      };

      this.show(
        {
          title,
          content: `<p class="confirm-message">${escapeHtml(message)}</p>`,
          footer: `
            <button class="btn btn-secondary" id="cancelBtn">Cancel</button>
            <button class="btn ${danger ? 'btn-danger' : 'btn-primary'}" id="confirmBtn">${escapeHtml(confirmText)}</button>
          `,
        },
        () => settle(false),
      );

      document.getElementById('cancelBtn')?.addEventListener('click', () => settle(false));
      document.getElementById('confirmBtn')?.addEventListener('click', () => settle(true));
    });
  },

  prompt({ title, label, value = '', placeholder = '', confirmText = 'Save', type = 'text' }) {
    return new Promise((resolve) => {
      const settle = (result) => {
        this.hide();
        resolve(result);
      };

      this.show(
        {
          title,
          content: `
            <div class="form-group">
              <label class="form-label" for="promptInput">${escapeHtml(label)}</label>
              <input class="form-input" id="promptInput" type="${escapeHtml(type)}"
                     value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}" />
            </div>
          `,
          footer: `
            <button class="btn btn-secondary" id="promptCancel">Cancel</button>
            <button class="btn btn-primary" id="promptOk">${escapeHtml(confirmText)}</button>
          `,
        },
        () => settle(null),
      );

      const input = document.getElementById('promptInput');
      input?.select();

      const submit = () => settle(input?.value ?? null);

      document.getElementById('promptCancel')?.addEventListener('click', () => settle(null));
      document.getElementById('promptOk')?.addEventListener('click', submit);
      input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') submit();
      });
    });
  },

  // ---------------------------------------------------------------------
  // Syllabus import
  //
  // One flow, no modes. The previous version made the user pick between
  // "Smart Parse" and "AI Parse" — a choice they had no basis to make, phrased
  // in terms of our implementation rather than their goal.
  //
  // Now the app escalates on their behalf, cheapest first:
  //
  //   1. Parse in the browser as they type. Instant, free, private. If the
  //      weights reconcile to 100% we are done and nothing leaves the device.
  //   2. Only if that is inconclusive, and only for signed-in users, ask the
  //      server — which itself retries its own stronger parser and a shared
  //      cache before ever reaching Claude.
  //   3. Fall back to whatever the local pass did find, so the user gets
  //      something to correct rather than a dead end.
  //
  // The result reports which path produced it, so AI is a visible outcome
  // rather than an upfront decision.
  // ---------------------------------------------------------------------

  /** Mirrors the server's confidence rule: categories found, and weights reconciling to 100%. */
  _isConfident(result) {
    if (!result || result.categories.length === 0) return false;

    const total = result.categories.reduce((sum, category) => sum + Number(category.weight), 0);

    return Math.abs(total - 100) < 0.5;
  },

  /**
   * @param {{canUseServer?: boolean}} options canUseServer is false for guests — the server
   *   path is metered per account, so an unattributable call would be an unmetered one.
   */
  showSyllabusPaste({ canUseServer = true } = {}) {
    return new Promise((resolve) => {
      const settle = (result) => {
        this.hide();
        resolve(result);
      };

      const host = ensureContainer();

      const secondPass = canUseServer
        ? '; anything unusual gets a second pass on the server'
        : '';

      host.innerHTML = `
        <div class="modal-backdrop">
          <div class="modal modal-lg" role="dialog" aria-modal="true" aria-label="Import from syllabus">
            <div class="modal-header">
              <h2 class="modal-title">Import from Syllabus</h2>
            </div>
            <div class="modal-body" id="syllabusModalBody">
              <p style="color: var(--color-text-muted); margin-bottom: var(--spacing-4); font-size: var(--font-size-sm);">
                Paste your syllabus and we will pull out the grading breakdown. Most are read
                instantly in your browser${secondPass}.
              </p>

              <textarea class="syllabus-textarea" id="syllabusText" placeholder="Paste your syllabus here...

Example:
MATH 101 - Introduction to Calculus
3 Credit Hours

Grading:
- Homework: 25%
- Quizzes: 15%
- Midterm Exam: 25%
- Final Exam: 35%

Grade Scale:
A: 90-100%
B: 80-89%
C: 70-79%"></textarea>

              <div id="parsePreview"></div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" id="skipSyllabus">Skip</button>
              <button class="btn btn-primary" id="parseSyllabus">Import</button>
            </div>
          </div>
        </div>
      `;

      host.style.display = 'block';
      bindDismiss(() => settle(null));

      const textarea = document.getElementById('syllabusText');
      textarea?.focus();

      let debounce;

      textarea?.addEventListener('input', () => {
        clearTimeout(debounce);
        // Debounced so a long syllabus is not re-parsed on every keystroke.
        debounce = setTimeout(() => this._renderParsePreview(textarea.value), 350);
      });

      document.getElementById('skipSyllabus')?.addEventListener('click', () => settle(null));

      document.getElementById('parseSyllabus')?.addEventListener('click', async () => {
        const text = textarea?.value.trim() ?? '';

        if (!text) {
          this.toast('Paste your syllabus text first.');
          return;
        }

        const local = parseSyllabusLocally(text);

        // Tier 1 — confident local read. No network, no account, no cost.
        if (this._isConfident(local)) {
          settle({ ...local, source: 'local' });
          return;
        }

        // Tier 2 — let the server try harder. It runs its own parser and a shared cache
        // before spending anything on Claude, so this is not automatically a paid call.
        if (canUseServer) {
          this._renderServerAttempt();

          try {
            settle(await SyllabusService.parse(text));
            return;
          } catch (error) {
            // Tier 3 — a partial local read still beats a dead end.
            if (local.categories.length > 0) {
              this.toast('Imported what we could find — please check the weights.');
              settle({ ...local, source: 'local-partial' });
              return;
            }

            this._renderFailure(error, settle);
            return;
          }
        }

        if (local.categories.length > 0) {
          this.toast('Imported what we could find — please check the weights.');
          settle({ ...local, source: 'local-partial' });
          return;
        }

        this.toast('No grading categories found. Try pasting just the grading section, or add them by hand.');
      });
    });
  },

  _renderParsePreview(text) {
    const preview = document.getElementById('parsePreview');
    if (!preview) return;

    if (!text.trim()) {
      preview.innerHTML = '';
      return;
    }

    const result = parseSyllabusLocally(text);

    if (result.categories.length === 0) {
      // Stay quiet rather than saying "found nothing" — they may still be pasting.
      preview.innerHTML = '';
      return;
    }

    const rows = [];

    if (result.className) {
      rows.push(`<div class="parse-preview-item"><span>Class</span><span class="parse-preview-value">${escapeHtml(result.className)}</span></div>`);
    }

    if (result.creditHours !== null) {
      rows.push(`<div class="parse-preview-item"><span>Credits</span><span class="parse-preview-value">${escapeHtml(result.creditHours)}</span></div>`);
    }

    for (const category of result.categories) {
      rows.push(`<div class="parse-preview-item"><span>${escapeHtml(category.name)}</span><span class="parse-preview-value">${escapeHtml(category.weight)}%</span></div>`);
    }

    if (result.gradeScale) {
      rows.push('<div class="parse-preview-item"><span>Grade Scale</span><span class="parse-preview-value">Custom detected</span></div>');
    }

    for (const note of result.notes) {
      rows.push(`<div class="parse-preview-item"><span style="color: var(--color-text-muted);">${escapeHtml(note)}</span><span></span></div>`);
    }

    preview.innerHTML = `<div class="parse-preview"><div class="parse-preview-title">Detected Info</div>${rows.join('')}</div>`;
  },

  /**
   * Status messages shown while the server works, advanced every
   * {@link PROGRESS_INTERVAL_MS}.
   *
   * A spinner that never changes reads as a hung page, and people abandon it. Rotating status
   * keeps a slow request feeling alive and measurably improves how long users wait.
   *
   * Every line here is true of what the server is actually doing, in the order it does it. That
   * is not squeamishness — it is what makes the technique work. By the time the first rotation
   * fires the deterministic pass and the cache have both missed, so the request really is with
   * the AI. Invented progress ("Analysing 12 categories...") would read the same for six
   * seconds and then be contradicted by whatever came back.
   *
   * The last line sets an expectation rather than claiming to be nearly done. A user told "up
   * to a minute" waits; a user told "almost there" three times stops believing the app.
   */
  _progressMessages: [
    'Reading your syllabus...',
    'This one needs a closer look — asking AI...',
    'Working through the grading section...',
    'Complex syllabi take a little longer...',
    'Still going — this can take up to a minute...',
  ],

  _progressTimer: null,

  /**
   * Advances the status message on a timer.
   *
   * The interval is stored on the module so {@link hide} can always clear it. A timer left
   * running after the dialog closes would fire against detached nodes forever.
   */
  _startProgress() {
    this._stopProgress();

    let index = 0;

    this._progressTimer = setInterval(() => {
      // Hold on the final line rather than looping. Cycling back to "Reading your syllabus"
      // after thirty seconds would suggest the work restarted.
      if (index >= this._progressMessages.length - 1) return;

      index += 1;

      const element = document.getElementById('syllabusProgress');
      if (!element) {
        // The dialog closed without going through hide(); stop rather than leak.
        this._stopProgress();
        return;
      }

      // Fade out, swap, fade in — an instant text change is jarring enough to look like a bug.
      element.classList.add('is-fading');

      setTimeout(() => {
        element.textContent = this._progressMessages[index];
        element.classList.remove('is-fading');
      }, PROGRESS_FADE_MS);
    }, PROGRESS_INTERVAL_MS);
  },

  _stopProgress() {
    if (this._progressTimer === null) return;

    clearInterval(this._progressTimer);
    this._progressTimer = null;
  },

  _renderServerAttempt() {
    const body = document.getElementById('syllabusModalBody');

    if (body) {
      body.innerHTML = `
        <div class="ai-processing">
          <div class="loading-spinner"></div>
          <p class="ai-progress" id="syllabusProgress" role="status" aria-live="polite">
            ${escapeHtml(this._progressMessages[0])}
          </p>
          <p style="color: var(--color-text-muted); font-size: var(--font-size-xs); margin-top: var(--spacing-2);">
            You can keep this open — we will fill everything in for you
          </p>
        </div>
      `;
    }

    const footer = ensureContainer().querySelector('.modal-footer');
    if (footer) footer.style.display = 'none';

    this._startProgress();
  },

  _renderFailure(error, settle) {
    const body = document.getElementById('syllabusModalBody');

    if (body) {
      body.innerHTML = `
        <div style="text-align: center; padding: var(--spacing-8);">
          <p style="color: var(--color-danger); font-size: var(--font-size-lg); margin-bottom: var(--spacing-3);">
            Could not read that syllabus
          </p>
          <p style="color: var(--color-text-muted); font-size: var(--font-size-sm); margin-bottom: var(--spacing-4);">
            ${escapeHtml(error?.message ?? 'Try pasting just the grading section, or add your categories by hand.')}
          </p>
        </div>
      `;
    }

    const footer = ensureContainer().querySelector('.modal-footer');

    if (footer) {
      footer.style.display = 'flex';
      footer.innerHTML = `
        <button class="btn btn-secondary" id="skipAfterError">Enter Manually</button>
        <button class="btn btn-primary" id="retrySyllabus">Try Again</button>
      `;

      document.getElementById('skipAfterError')?.addEventListener('click', () => settle(null));
      document.getElementById('retrySyllabus')?.addEventListener('click', () => {
        this.hide();
        this.showSyllabusPaste().then(settle);
      });
    }
  },

  // ---------------------------------------------------------------------
  // Gradebook import
  // ---------------------------------------------------------------------

  showGradebookImport() {
    return new Promise((resolve) => {
      const settle = (result) => {
        this.hide();
        resolve(result);
      };

      const host = ensureContainer();

      host.innerHTML = `
        <div class="modal-backdrop">
          <div class="modal modal-lg" role="dialog" aria-modal="true" aria-label="Import grades">
            <div class="modal-header">
              <h2 class="modal-title">Import Grades</h2>
            </div>
            <div class="modal-body">
              <p style="color: var(--color-text-muted); margin-bottom: var(--spacing-4); font-size: var(--font-size-sm);">
                Paste grades from Canvas, Blackboard, or any spreadsheet. Supports CSV, tab-separated, or "Name: score/total".
              </p>
              <textarea class="syllabus-textarea" id="gradebookText" style="min-height: 220px;" placeholder="Paste your grades here...

Examples:
Homework 1: 85/100
Quiz 1: 18/20

Or CSV:
Assignment, Score, Possible
Homework 1, 85, 100"></textarea>
              <div id="gradebookPreview"></div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-secondary" id="cancelImport">Cancel</button>
              <button class="btn btn-primary" id="importGrades">Import Grades</button>
            </div>
          </div>
        </div>
      `;

      host.style.display = 'block';
      bindDismiss(() => settle([]));

      const textarea = document.getElementById('gradebookText');
      textarea?.focus();

      let debounce;

      textarea?.addEventListener('input', () => {
        clearTimeout(debounce);

        debounce = setTimeout(() => {
          const grades = parseGradebook(textarea.value);
          const preview = document.getElementById('gradebookPreview');
          if (!preview) return;

          if (grades.length === 0) {
            preview.innerHTML = '';
            return;
          }

          const shown = grades.slice(0, 8).map((grade) => `
            <div class="parse-preview-item">
              <span>${escapeHtml(grade.name)}</span>
              <span class="parse-preview-value">${grade.pointsEarned ?? '—'}/${escapeHtml(grade.pointsPossible)}</span>
            </div>
          `).join('');

          const overflow = grades.length > 8
            ? `<div class="parse-preview-item"><span style="color: var(--color-text-muted);">...and ${grades.length - 8} more</span><span></span></div>`
            : '';

          preview.innerHTML = `
            <div class="parse-preview">
              <div class="parse-preview-title">Found ${grades.length} grade${grades.length === 1 ? '' : 's'}</div>
              ${shown}${overflow}
            </div>
          `;
        }, 400);
      });

      document.getElementById('cancelImport')?.addEventListener('click', () => settle([]));

      document.getElementById('importGrades')?.addEventListener('click', () => {
        const grades = parseGradebook(textarea?.value ?? '');

        if (grades.length > 0) settle(grades);
        else this.toast('No grades detected. Check the format and try again.');
      });
    });
  },

  /** Transient status message. Announced politely so screen readers pick it up. */
  toast(message) {
    document.querySelector('.modal-toast')?.remove();

    const toast = document.createElement('div');
    toast.className = 'modal-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;

    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3200);
  },
};
